<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div v-if="accessDeniedByApi" class="row m-4 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0 text-center text-muted py-3">
          {{ helper.trans('statistic.counterparties.access_denied') }}
        </div>
      </div>
    </div>

    <template v-else>
      <div class="row m-4 mb-0 mt-0">
        <div class="col-sm-12">
          <div class="card card-body mb-3">
            <div class="row mt-2">
              <div v-if="isAdmin || isOperator" class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="requestDTO.id"
                  :placeholder="helper.trans('labels.id')"
                  @keydown.enter.prevent="applyFilters"
                />
              </div>
              <div v-if="isAdmin || isOperator" class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model="requestDTO.query"
                  :placeholder="helper.trans('labels.requisites')"
                  @keydown.enter.prevent="applyFilters"
                />
              </div>
              <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <Multiselect
                  id="cp-filter-currencies"
                  class="multiselect-sm"
                  v-model="requestDTO.currencies"
                  :options="currencyList"
                  :custom-label="(opt) => `${opt}`"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.currencies')"
                />
              </div>
              <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <Multiselect
                  id="cp-filter-methods"
                  class="multiselect-sm"
                  v-model="requestDTO.methods"
                  :options="methodList()"
                  :custom-label="(opt) => helper.trans(`pay_methods.${opt}`)"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.payment_methods')"
                >
                  <template #option="props">
                    <div class="d-flex flex-row align-items-center gap-2">
                      <PayMethodIcon icon-class="icon" :method="props.option" />
                      <span class="overflow-hidden" style="white-space: nowrap; text-overflow: ellipsis">{{ helper.trans('pay_methods.' + props.option) }}</span>
                    </div>
                  </template>
                </Multiselect>
              </div>
              <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <Multiselect
                  id="cp-filter-options"
                  class="multiselect-sm"
                  v-model="requestDTO.options"
                  :options="paymentOptions"
                  :custom-label="(opt) => helper.trans(`payment_options.${opt}`)"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.payment_options')"
                />
              </div>
              <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <Multiselect
                  id="cp-filter-statuses"
                  class="multiselect-sm"
                  v-model="requestDTO.statuses"
                  :options="[1, 0]"
                  :custom-label="(opt) => (opt === 1 ? helper.trans('requisites.active') : helper.trans('requisites.inactive'))"
                  :multiple="true"
                  select-label="Select"
                  :placeholder="helper.trans('labels.status')"
                />
              </div>
              <div v-if="isOperator || isAdmin || isTeamLead" class="form-group mb-2 col-md-3 col-lg-3 col-xl-2 me-2">
                <TraderMultiselect :multiple="true" v-model="tradersFilter" />
              </div>
              <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <StoreMultiselect :multiple="false" v-model="filterStore" />
              </div>
              <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  v-model.trim="requestDTO.counterpartyUserId"
                  :placeholder="helper.trans('labels.user_id')"
                  autocomplete="off"
                  @keydown.enter.prevent="applyFilters"
                />
              </div>
              <div class="form-group mb-2 col">
                <div class="col-auto p-0 text-end">
                  <button type="button" @click.prevent="applyFilters" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">{{ helper.trans('button.apply') }}</button>
                  <button type="button" @click.prevent="clearFilters" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first">{{ helper.trans('button.reset') }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row m-4 mb-0 mt-0">
        <div class="col-sm-12">
          <div class="card card-body mb-0">
            <div class="overflow-auto">
              <table class="table table-striped border-light align-middle text-left text-nowrap position-relative mb-0">
                <thead>
                  <tr>
                    <th style="width: 0"></th>
                    <th class="text-capitalize-first">{{ helper.trans('statistic.counterparties.profile') }}</th>
                    <th class="text-capitalize-first text-end">{{ helper.trans('statistic.counterparties.profile_total_amount') }}</th>
                    <th class="text-capitalize-first text-end">{{ helper.trans('statistic.counterparties.profile_average_check') }}</th>
                    <th class="text-capitalize-first text-end">{{ helper.trans('statistic.counterparties.profile_avg_traffic_24h') }}</th>
                    <th class="text-capitalize-first text-end">{{ helper.trans('statistic.counterparties.profile_deals') }}</th>
                    <th class="text-capitalize-first text-end">{{ helper.trans('statistic.counterparties.profile_days_in_work') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="item in requisites.items" :key="item.id">
                    <tr>
                      <td>
                        <button type="button" class="btn btn-sm btn-link p-0" @click.prevent="toggleExpand(item.id)">
                          <i class="fa-solid" :class="isExpanded(item.id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                        </button>
                      </td>
                      <td>
                        <span class="d-inline-flex align-items-center gap-2">
                          <PayMethodIcon :method="item.method"></PayMethodIcon>
                          <span>#{{ item.id }}</span>
                          <span v-if="item.requisites" class="text-muted">{{ item.requisites }}</span>
                        </span>
                      </td>
                      <td class="text-end">
                        <span v-if="profileLoading[item.id]" class="text-muted">…</span>
                        <span v-else>{{ formatProfileTotalAmount(item.id, item.currency) }}</span>
                      </td>
                      <td class="text-end">
                        <span v-if="profileLoading[item.id]" class="text-muted">…</span>
                        <span v-else>{{ formatProfileAverageCheck(item.id) }}</span>
                      </td>
                      <td class="text-end">
                        <span v-if="profileLoading[item.id]" class="text-muted">…</span>
                        <span v-else>{{ formatProfileAvgTraffic24h(item) }}</span>
                      </td>
                      <td class="text-end">
                        <span v-if="profileLoading[item.id]" class="text-muted">…</span>
                        <span v-else>{{ formatProfileDeals(item.id) }}</span>
                      </td>
                      <td class="text-end">
                        <span v-if="profileLoading[item.id]" class="text-muted">…</span>
                        <span v-else>{{ formatProfileDaysInWork(item.id) }}</span>
                      </td>
                    </tr>
                    <tr v-if="isExpanded(item.id)">
                      <td colspan="7" class="p-0 bg-light">
                        <div class="p-3">
                          <div class="d-flex justify-content-end mb-2">
                            <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" @click.prevent="openAttach(item)">
                              {{ helper.trans('statistic.counterparties.attach') }}
                            </button>
                          </div>
                          <div v-if="expandLoading[item.id]" class="text-center text-muted py-3">
                            {{ helper.trans('requisites_capacity.loading') }}
                          </div>
                          <div v-else class="overflow-auto">
                            <table class="table table-striped border-light align-middle text-left text-nowrap bg-white mb-0">
                              <thead>
                                <tr>
                                  <th class="text-capitalize-first">{{ helper.trans('statistic.counterparties.counterparty_id') }}</th>
                                  <th class="text-capitalize-first">{{ helper.trans('statistic.counterparties.sent') }}</th>
                                  <th class="text-capitalize-first">{{ helper.trans('labels.average_check') }}</th>
                                  <th class="text-capitalize-first">{{ helper.trans('labels.conversion_rate') }}</th>
                                  <th class="text-capitalize-first">{{ helper.trans('labels.total_completed') }}</th>
                                  <th class="text-capitalize-first">{{ helper.trans('labels.last_used_at') }}</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr v-for="(cp, cpIndex) in getCounterparties(item.id)" :key="`${cp.userId}-${cpIndex}`">
                                  <td>
                                    <span v-b-tooltip.hover :title="cp.userId">{{ cp.userId }}</span>
                                    <span v-if="cp.isBanned" class="badge bg-danger ms-1">{{ helper.trans('labels.banned') }}</span>
                                  </td>
                                  <td>{{ formatSent(cp) }}</td>
                                  <td>{{ numberFormatter.formatNumber(Number.parseFloat(cp.averageCheck || '0'), 2) }}</td>
                                  <td>{{ formatConversion(cp) }}</td>
                                  <td>{{ numberFormatter.formatNumber(cp.totalCompleted ?? cp.completedCount) }}</td>
                                  <td>{{ formatLastUsedAt(cp.lastUsedAt) }}</td>
                                  <td class="text-end">
                                    <div class="dropdown">
                                      <button
                                        type="button"
                                        class="btn btn-sm btn-outline-dark"
                                        data-bs-toggle="dropdown"
                                        v-bs-dropdown-fixed
                                        aria-expanded="false"
                                      >
                                        <i class="fa-solid fa-ellipsis"></i>
                                      </button>
                                      <ul class="dropdown-menu dropdown-menu-end">
                                        <li>
                                          <a href="#" class="dropdown-item" @click.prevent="viewStats(cp)">
                                            <i class="fa fa-eye me-2"></i>{{ helper.trans('statistic.counterparties.menu_view_stats') }}
                                          </a>
                                        </li>
                                        <li>
                                          <a href="#" class="dropdown-item" @click.prevent="startReattach(item.id, cp.userId)">
                                            <i class="fa fa-right-left me-2"></i>{{ helper.trans('statistic.counterparties.menu_reattach') }}
                                          </a>
                                        </li>
                                        <li>
                                          <a href="#" class="dropdown-item text-danger" @click.prevent="confirmUnlink(item.id, cp)">
                                            <i class="fa fa-trash me-2"></i>{{ helper.trans('statistic.counterparties.menu_unlink') }}
                                          </a>
                                        </li>
                                      </ul>
                                    </div>
                                  </td>
                                </tr>
                                <tr v-if="!getCounterparties(item.id).length">
                                  <td :colspan="7" class="text-center text-muted py-3">
                                    {{ helper.trans('labels.no_history') }}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!requisites.items.length">
                    <td colspan="7" class="text-center text-muted py-4">
                      {{ helper.trans('statistic.counterparties.no_requisites') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <b-pagination
              v-if="requisites.totalCount > requisites.limit"
              v-model="page"
              :total-rows="requisites.totalCount"
              :per-page="requisites.limit"
              :limit="20"
              @update:modelValue="loadRequisites"
              prev-text="Back"
              next-text="Next"
              align="start"
              class="mt-3"
              first-number
              hide-goto-end-buttons
              hide-ellipsis
            />
          </div>
        </div>
      </div>
    </template>

    <b-modal
      :title="helper.trans('statistic.counterparties.attach')"
      :modelValue="attachModalVisible"
      footer-class="d-flex justify-content-end gap-2"
      @update:modelValue="attachModalVisible = $event"
    >
      <template #default>
        <div class="mb-2">
          <label class="form-label text-capitalize-first">{{ helper.trans('labels.stores') }}</label>
          <StoreMultiselect :multiple="false" v-model="attachStore" />
        </div>
        <div class="mb-2">
          <label class="form-label text-capitalize-first">{{ helper.trans('labels.user_id') }}</label>
          <input v-model.trim="attachUserId" type="text" class="form-control form-control-sm" autocomplete="off" />
        </div>
        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" :disabled="!attachStore" @click.prevent="openImportForAttach">
          {{ helper.trans('statistic.counterparties.import_from_file') }}
        </button>
      </template>
      <template #footer>
        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click.prevent="closeAttachModal">
          {{ helper.trans('button.cancel') }}
        </button>
        <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" :disabled="!attachUserId || !attachStore" @click.prevent="submitAttach">
          {{ helper.trans('button.save') }}
        </button>
      </template>
    </b-modal>

    <CounterpartyStatisticsDetailsModal ref="detailsModal" />
    <ConfirmModal ref="confirmModal" />
    <ReattachRequisiteModal ref="reattachModal" @selected="handleReattachSelected" />
    <CounterpartiesImportModal ref="importModal" @imported="onImported" />
  </LoaderWrapper>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, reactive, ref, toRefs} from 'vue';
import {useStore} from 'vuex';
import Multiselect from 'vue-multiselect';
import TraderMultiselect from '@/components/TraderMultiselect.vue';
import TraderDTO from '@/models/TraderDTO';
import axios from 'axios';
import moment from 'moment';
import {BPagination, BModal} from 'bootstrap-vue-3';
import {useNotification} from '@kyvg/vue3-notification';
import LoaderWrapper from '@/components/LoaderWrapper.vue';
import StoreMultiselect from '@/components/StoreMultiselect.vue';
import PayMethodIcon from '@/components/PayMethodIcon.vue';
import ConfirmModal, {ConfirmModalProps} from '@/components/ConfirmModal.vue';
import ReattachRequisiteModal from '@/components/ReattachRequisiteModal.vue';
import CounterpartiesImportModal from '@/components/CounterpartiesImportModal.vue';
import CounterpartyStatisticsDetailsModal from '@/components/CounterpartyStatisticsDetailsModal.vue';
import APIClient from '@/service/APIClient';
import {helper} from '@/service/CommonHelper';
import {dateHelper} from '@/service/DateHelper';
import {numberFormatter} from '@/service/NumberFormatter';
import {useCounterpartyManagementAccess} from '@/service/CounterpartyManagementAccess';
import PaginatedResponseDTO from '@/models/PaginatedResponseDTO';
import TraderRequisitesDTO from '@/models/TraderRequisitesDTO';
import RequisitesRequestDTO from '@/models/request/RequisitesRequestDTO';
import MerchantStoreDTO from '@/models/MerchantStoreDTO';
import CounterpartyStatisticsDTO from '@/models/CounterpartyStatisticsDTO';

interface RequisiteProfileSummary {
  totalAmount: number;
  currency: string;
  averageCheck: number;
  avgTraffic24h: number;
  deals: number;
  daysInWork: number | null;
}

const notification = useNotification();
const apiClient = new APIClient();

const defaultRequisitesRequest = (): RequisitesRequestDTO => ({
  id: null,
  ids: [],
  currencies: [],
  traderIds: [],
  methods: [],
  statuses: [],
  options: [],
  query: null,
  groupId: null,
  isDeleted: 'false',
  minAmount: null,
  maxAmount: null,
  deviceId: null,
  storeId: null,
  counterpartyUserId: null,
});

export default defineComponent({
  components: {
    LoaderWrapper,
    StoreMultiselect,
    PayMethodIcon,
    Multiselect,
    TraderMultiselect,
    BPagination,
    BModal,
    ConfirmModal,
    ReattachRequisiteModal,
    CounterpartiesImportModal,
    CounterpartyStatisticsDetailsModal,
  },
  setup() {
    const store = useStore();
    const {accessDeniedByApi, markAccessDenied} = useCounterpartyManagementAccess();

    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const confirmModal = ref<InstanceType<typeof ConfirmModal> | null>(null);
    const reattachModal = ref<InstanceType<typeof ReattachRequisiteModal> | null>(null);
    const importModal = ref<InstanceType<typeof CounterpartiesImportModal> | null>(null);
    const detailsModal = ref<InstanceType<typeof CounterpartyStatisticsDetailsModal> | null>(null);

    const state = reactive({
      page: 1,
      requestDTO: defaultRequisitesRequest(),
      tradersFilter: [] as TraderDTO[],
      filterStore: null as MerchantStoreDTO | null,
      requisites: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: [] as TraderRequisitesDTO[],
      } as PaginatedResponseDTO<TraderRequisitesDTO>,
      expandedIds: {} as Record<number, boolean>,
      counterpartiesCache: {} as Record<string, CounterpartyStatisticsDTO[]>,
      profileSummaryCache: {} as Record<string, RequisiteProfileSummary>,
      profileLoading: {} as Record<number, boolean>,
      expandLoading: {} as Record<number, boolean>,
      attachModalVisible: false,
      attachUserId: '',
      attachStore: null as MerchantStoreDTO | null,
      attachRequisite: null as TraderRequisitesDTO | null,
      reattachContext: null as { requisitesId: number; userId: string } | null,
    });

    const cacheKey = (requisitesId: number) => String(requisitesId);

    const computeProfileSummary = (
      item: TraderRequisitesDTO,
      cps: CounterpartyStatisticsDTO[],
    ): RequisiteProfileSummary => {
      let totalAmount = 0;
      let deals = 0;
      let earliestFirstUsedAt: string | null = null;

      for (const cp of cps) {
        totalAmount += Number.parseFloat(cp.totalAmount || '0') || 0;
        deals += cp.totalCompleted ?? cp.completedCount ?? 0;
        if (cp.firstUsedAt && (earliestFirstUsedAt === null || cp.firstUsedAt < earliestFirstUsedAt)) {
          earliestFirstUsedAt = cp.firstUsedAt;
        }
      }

      const counterpartyCount = cps.length;
      const avgTraffic24h =
        counterpartyCount > 0
          ? item.dayTradesCount / counterpartyCount
          : item.dayTradesCount;

      let daysInWork: number | null = null;
      if (earliestFirstUsedAt) {
        daysInWork = Math.max(0, moment().diff(moment(earliestFirstUsedAt), 'days'));
      }

      return {
        totalAmount,
        currency: cps[0]?.currency || item.currency,
        averageCheck: deals > 0 ? totalAmount / deals : 0,
        avgTraffic24h,
        deals,
        daysInWork,
      };
    };

    const applyProfileSummary = (requisitesId: number, item: TraderRequisitesDTO, cps: CounterpartyStatisticsDTO[]) => {
      state.profileSummaryCache[cacheKey(requisitesId)] = computeProfileSummary(item, cps);
    };

    const invalidateRequisiteCache = (requisitesId: number) => {
      delete state.counterpartiesCache[cacheKey(requisitesId)];
      delete state.profileSummaryCache[cacheKey(requisitesId)];
      const item = state.requisites.items.find((row) => row.id === requisitesId);
      if (!item) {
        return;
      }
      void fetchCounterparties(item, state.expandedIds[requisitesId] === true);
    };

    const handleCounterpartyError = (error: unknown): boolean => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        markAccessDenied();
        notification.notify({
          type: 'error',
          title: helper.trans('statistic.counterparties.access_denied'),
        });
        return true;
      }
      return false;
    };

    const counterpartiesStoreId = () => state.requestDTO.storeId ?? undefined;

    const syncRequestFromFilters = () => {
      state.requestDTO.traderIds = state.tradersFilter.map((trader) => trader.id);
      state.requestDTO.storeId = state.filterStore?.id ?? null;
    };

    const resetRequisitesCaches = () => {
      state.expandedIds = {};
      state.counterpartiesCache = {};
      state.profileSummaryCache = {};
      state.profileLoading = {};
      state.expandLoading = {};
    };

    const loadRequisites = async () => {
      loader.value?.show();
      try {
        state.requisites.offset = state.requisites.limit * (state.page - 1);
        syncRequestFromFilters();
        state.requisites = await apiClient.requisites(
          state.requisites,
          {createdAt: 'desc'},
          state.requestDTO,
        );
        resetRequisitesCaches();
        await loadProfileSummariesForPage();
      } finally {
        loader.value?.hide();
      }
    };

    const applyFilters = async () => {
      state.page = 1;
      await loadRequisites();
    };

    const clearFilters = async () => {
      state.page = 1;
      state.requestDTO = defaultRequisitesRequest();
      state.tradersFilter = [];
      state.filterStore = null;
      await loadRequisites();
    };

    const fetchCounterparties = async (item: TraderRequisitesDTO, expanded: boolean) => {
      if (expanded) {
        state.expandLoading[item.id] = true;
      } else {
        state.profileLoading[item.id] = true;
      }
      try {
        const list = await apiClient.requisitesCounterparties(item.id, counterpartiesStoreId());
        state.counterpartiesCache[cacheKey(item.id)] = list;
        applyProfileSummary(item.id, item, list);
      } catch (error) {
        state.counterpartiesCache[cacheKey(item.id)] = [];
        applyProfileSummary(item.id, item, []);
        handleCounterpartyError(error);
      } finally {
        state.expandLoading[item.id] = false;
        state.profileLoading[item.id] = false;
      }
    };

    const loadProfileSummariesForPage = async () => {
      await Promise.all(state.requisites.items.map((item) => fetchCounterparties(item, false)));
    };

    const getProfileSummary = (requisitesId: number) => state.profileSummaryCache[cacheKey(requisitesId)];

    const formatProfileTotalAmount = (requisitesId: number, fallbackCurrency: string) => {
      const summary = getProfileSummary(requisitesId);
      if (!summary || summary.totalAmount <= 0) {
        return '—';
      }
      const amount = numberFormatter.formatNumber(summary.totalAmount, 2);
      const currency = summary.currency || fallbackCurrency;
      return currency ? `${amount} ${currency}` : amount;
    };

    const formatProfileAverageCheck = (requisitesId: number) => {
      const summary = getProfileSummary(requisitesId);
      if (!summary || summary.averageCheck <= 0) {
        return '—';
      }
      return numberFormatter.formatNumber(summary.averageCheck, 2);
    };

    const formatProfileAvgTraffic24h = (item: TraderRequisitesDTO) => {
      const summary = getProfileSummary(item.id);
      if (!summary) {
        return '—';
      }
      return numberFormatter.formatNumber(summary.avgTraffic24h, 2);
    };

    const formatProfileDeals = (requisitesId: number) => {
      const summary = getProfileSummary(requisitesId);
      if (!summary) {
        return '—';
      }
      return numberFormatter.formatNumber(summary.deals);
    };

    const formatProfileDaysInWork = (requisitesId: number) => {
      const summary = getProfileSummary(requisitesId);
      if (!summary || summary.daysInWork === null) {
        return '—';
      }
      return numberFormatter.formatNumber(summary.daysInWork);
    };

    const isExpanded = (id: number) => !!state.expandedIds[id];

    const toggleExpand = async (requisitesId: number) => {
      const next = !state.expandedIds[requisitesId];
      state.expandedIds[requisitesId] = next;
      if (next && !state.counterpartiesCache[cacheKey(requisitesId)]) {
        const item = state.requisites.items.find((row) => row.id === requisitesId);
        if (item) {
          await fetchCounterparties(item, true);
        }
      }
    };

    const getCounterparties = (requisitesId: number) =>
      state.counterpartiesCache[cacheKey(requisitesId)] ?? [];

    const formatSent = (cp: CounterpartyStatisticsDTO) => {
      if (!cp.totalAmount) {
        return '—';
      }
      return cp.currency ? `${cp.totalAmount} ${cp.currency}` : cp.totalAmount;
    };

    const formatConversion = (cp: CounterpartyStatisticsDTO) => {
      const rate = cp.totalConversionRate ?? cp.conversionRate;
      return `${numberFormatter.formatNumber(rate, 2)}%`;
    };

    const formatLastUsedAt = (date: string) => {
      if (!date) {
        return '—';
      }
      const tz = dateHelper.getUserTimeZone();
      const m = moment(date).utcOffset(0).tz(tz);
      const today = moment().tz(tz);
      if (m.isSame(today, 'day')) {
        return helper.trans('statistic.counterparties.today_at', {time: m.format('HH:mm')});
      }
      return dateHelper.format(date, 'YYYY-MM-DD HH:mm');
    };

    const openAttach = (item: TraderRequisitesDTO) => {
      state.attachRequisite = item;
      state.attachUserId = '';
      state.attachStore = null;
      state.attachModalVisible = true;
    };

    const closeAttachModal = () => {
      state.attachModalVisible = false;
      state.attachRequisite = null;
      state.attachUserId = '';
      state.attachStore = null;
    };

    const submitAttach = async () => {
      if (!state.attachRequisite || !state.attachStore || !state.attachUserId) {
        return;
      }
      loader.value?.show();
      try {
        await apiClient.attachRequisitesCounterparty(state.attachRequisite.id, {
          storeId: state.attachStore.id,
          userId: state.attachUserId,
        });
        const requisitesId = state.attachRequisite.id;
        notification.notify({type: 'success', title: helper.trans('statistic.counterparties.attach_success')});
        closeAttachModal();
        invalidateRequisiteCache(requisitesId);
      } catch (error: any) {
        if (handleCounterpartyError(error)) {
          return;
        }
        const status = error?.response?.status;
        if (status === 404) {
          notification.notify({type: 'error', title: helper.trans('statistic.counterparties.attach_not_found')});
        } else {
          notification.notify({
            type: 'error',
            title: error?.response?.data?.message || helper.trans('labels.error'),
          });
        }
      } finally {
        loader.value?.hide();
      }
    };

    const openImportForAttach = () => {
      if (!state.attachRequisite || !state.attachStore) {
        return;
      }
      const requisite = state.attachRequisite;
      const storeItem = state.attachStore;
      state.attachModalVisible = false;
      importModal.value?.show({requisite, store: storeItem});
    };

    const onImported = (requisitesId: number) => {
      invalidateRequisiteCache(requisitesId);
      state.attachRequisite = null;
    };

    const viewStats = (cp: CounterpartyStatisticsDTO) => {
      detailsModal.value?.show(cp);
    };

    const confirmUnlink = (requisitesId: number, item: CounterpartyStatisticsDTO) => {
      confirmModal.value?.show({
        title: helper.trans('labels.unlink_counterparty_confirm'),
        okTitle: helper.trans('statistic.counterparties.menu_unlink'),
        cancelTitle: helper.trans('button.cancel'),
        onOkClick: async () => {
          await unlinkCounterparty(requisitesId, item);
        },
      } as ConfirmModalProps);
    };

    const unlinkCounterparty = async (requisitesId: number, item: CounterpartyStatisticsDTO) => {
      loader.value?.show();
      try {
        await apiClient.deleteRequisitesCounterparty(requisitesId, item.userId, item.storeId);
        notification.notify({type: 'success', title: helper.trans('labels.counterparty_unlinked')});
        invalidateRequisiteCache(requisitesId);
      } catch (error: any) {
        if (!handleCounterpartyError(error)) {
          notification.notify({
            type: 'error',
            title: error?.response?.data?.message || helper.trans('labels.error'),
          });
        }
      } finally {
        loader.value?.hide();
      }
    };

    const startReattach = async (requisitesId: number, userId: string) => {
      try {
        const list = await apiClient.getAvailableRequisitesForReattach(requisitesId, userId);
        if (!list.length) {
          notification.notify({type: 'warn', title: helper.trans('labels.no_available_requisites')});
          return;
        }
        state.reattachContext = {requisitesId, userId};
        reattachModal.value?.show(userId, list);
      } catch (error: any) {
        if (!handleCounterpartyError(error)) {
          notification.notify({
            type: 'error',
            title: error?.response?.data?.message || helper.trans('labels.error'),
          });
        }
      }
    };

    const handleReattachSelected = async (userId: string, newRequisitesId: number) => {
      const ctx = state.reattachContext;
      if (!ctx || ctx.userId !== userId) {
        return;
      }
      loader.value?.show();
      try {
        await apiClient.reattachCounterparty(ctx.requisitesId, userId, newRequisitesId);
        notification.notify({type: 'success', title: helper.trans('labels.reattach_success')});
        invalidateRequisiteCache(ctx.requisitesId);
        if (state.expandedIds[newRequisitesId]) {
          invalidateRequisiteCache(newRequisitesId);
        }
        state.reattachContext = null;
      } catch (error: any) {
        if (!handleCounterpartyError(error)) {
          notification.notify({
            type: 'error',
            title: error?.response?.data?.message || helper.trans('labels.reattach_error'),
          });
        }
      } finally {
        loader.value?.hide();
      }
    };

    onMounted(async () => {
      await loadRequisites();
    });

    return {
      ...toRefs(state),
      loader,
      helper,
      dateHelper,
      numberFormatter,
      accessDeniedByApi,
      isAdmin: computed(() => store.getters.isAdmin),
      isOperator: computed(() => store.getters.isOperator),
      isTeamLead: computed(() => store.getters.isTeamLead),
      currencyList: computed(() => store.getters['currencyMethods/currencies']),
      methodList: computed(() => store.getters['currencyMethods/methods']),
      paymentOptions: computed(() => store.getters['currencyMethods/options']()),
      applyFilters,
      clearFilters,
      loadRequisites,
      isExpanded,
      toggleExpand,
      getCounterparties,
      formatProfileTotalAmount,
      formatProfileAverageCheck,
      formatProfileAvgTraffic24h,
      formatProfileDeals,
      formatProfileDaysInWork,
      formatSent,
      formatConversion,
      formatLastUsedAt,
      openAttach,
      closeAttachModal,
      submitAttach,
      openImportForAttach,
      onImported,
      viewStats,
      confirmUnlink,
      startReattach,
      handleReattachSelected,
      confirmModal,
      reattachModal,
      importModal,
      detailsModal,
    };
  },
});
</script>
